import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'madkpi_chat_conversations';
const MAX_CONVERSATIONS = 15;

// Helper to serialize conversations for storage
const serializeConversations = (conversations) => {
  return JSON.stringify(
    conversations.map(conv => ({
      ...conv,
      timestamp: conv.timestamp instanceof Date ? conv.timestamp.toISOString() : conv.timestamp
    }))
  );
};

// Helper to deserialize conversations from storage
const deserializeConversations = (json) => {
  try {
    const parsed = JSON.parse(json);
    return parsed.map(conv => ({
      ...conv,
      timestamp: new Date(conv.timestamp)
    }));
  } catch (error) {
    console.error('Error parsing conversations from localStorage:', error);
    return [];
  }
};

// Hook for managing conversation history with localStorage persistence
export const useConversationHistory = () => {
  const [conversations, setConversations] = useState(() => {
    // Initialize from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return deserializeConversations(stored);
      }
    } catch (error) {
      console.error('Error loading conversations from localStorage:', error);
    }
    return [];
  });
  const [activeConversation, setActiveConversation] = useState(null);

  // Save to localStorage whenever conversations change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, serializeConversations(conversations));
    } catch (error) {
      console.error('Error saving conversations to localStorage:', error);
      // Handle storage quota exceeded
      if (error.name === 'QuotaExceededError') {
        // Remove oldest conversations and try again
        const reduced = conversations.slice(0, Math.floor(MAX_CONVERSATIONS / 2));
        try {
          localStorage.setItem(STORAGE_KEY, serializeConversations(reduced));
          setConversations(reduced);
        } catch (retryError) {
          console.error('Failed to save even after reducing conversations:', retryError);
        }
      }
    }
  }, [conversations]);

  const createNewConversation = useCallback((question) => {
    const newConversation = {
      id: Date.now().toString(),
      title: question.length > 50 ? question.substring(0, 50) + '...' : question,
      question,
      blocks: [],
      timestamp: new Date(),
    };

    setConversations(prev => {
      // Keep only MAX_CONVERSATIONS (15) most recent conversations
      const updated = [newConversation, ...prev.slice(0, MAX_CONVERSATIONS - 1)];
      return updated;
    });

    setActiveConversation(newConversation.id);
    return newConversation.id;
  }, []);

  const updateConversationBlocks = useCallback((conversationId, blocks) => {
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, blocks: [...blocks] }
          : conv
      )
    );
  }, []);

  const selectConversation = useCallback((conversationId) => {
    setActiveConversation(conversationId);
  }, []);

  const deleteConversation = useCallback((conversationId) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    if (activeConversation === conversationId) {
      setActiveConversation(null);
    }
  }, [activeConversation]);

  const getCurrentConversation = useCallback(() => {
    if (!activeConversation) return null;
    return conversations.find(conv => conv.id === activeConversation) || null;
  }, [conversations, activeConversation]);

  const startNewConversation = useCallback(() => {
    setActiveConversation(null);
  }, []);

  return {
    conversations,
    activeConversation,
    createNewConversation,
    updateConversationBlocks,
    selectConversation,
    deleteConversation,
    getCurrentConversation,
    startNewConversation,
  };
};